import React, { useState, useEffect } from 'react';
import { useAuth } from '../../components/Auth/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Star, MapPin, Camera, MessageCircle, Calendar } from 'lucide-react';

export default function ContributionsSection() {
  const { token } = useAuth();
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContributions();
  }, []);

  const fetchContributions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/contributions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setContributions(data);
      }
    } catch (error) {
      console.error('Error fetching contributions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getContributionIcon = (type) => {
    switch (type) {
      case 'spot':
        return <MapPin className="h-4 w-4" />;
      case 'photo':
        return <Camera className="h-4 w-4" />;
      case 'comment':
        return <MessageCircle className="h-4 w-4" />;
      default:
        return <Star className="h-4 w-4" />;
    }
  };

  const getContributionLabel = (type) => {
    switch (type) {
      case 'spot':
        return 'Spot créé';
      case 'photo':
        return 'Photo ajoutée';
      case 'comment':
        return 'Commentaire';
      default:
        return 'Contribution';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Chargement de vos contributions...</div>
        </CardContent>
      </Card>
    );
  }

  if (contributions.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center py-12">
            <Star className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600">Aucune contribution</h3>
            <p className="text-gray-500">
              Ajoutez des spots, photos ou commentaires pour contribuer à la communauté !
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Mes contributions ({contributions.length})</h2>

      <div className="space-y-4">
        {contributions.map((item) => (
          <Card key={item.contribution.id}>
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                {/* Spot Image */}
                {item.spot.photos && item.spot.photos.length > 0 && (
                  <div className="w-16 h-16 flex-shrink-0">
                    <img
                      src={item.spot.photos[0].url}
                      alt={item.spot.title}
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                )}

                {/* Contribution Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="secondary" className="text-xs">
                      {getContributionIcon(item.contribution.contribution_type)}
                      <span className="ml-1">
                        {getContributionLabel(item.contribution.contribution_type)}
                      </span>
                    </Badge>
                    <span className="text-xs text-gray-500">
                      {new Date(item.contribution.created_at).toLocaleDateString('fr-FR')}
                    </span>
                  </div>

                  <h4 className="font-semibold text-lg mb-1">{item.spot.title}</h4>
                  <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                    {item.spot.description}
                  </p>

                  {/* Spot Stats */}
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Star className="h-3 w-3" />
                      {item.spot.likes} likes
                    </span>
                    <span className="flex items-center gap-1">
                      <Camera className="h-3 w-3" />
                      {item.spot.photos?.length || 0} photos
                    </span>
                    <span className="flex items-center gap-1">
                      <MessageCircle className="h-3 w-3" />
                      {item.spot.comments?.length || 0} commentaires
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}